/**
 * useCustomerSession — customer authentication and session state for public menu.
 * Manages: localStorage session, favorites, reorder, auth modal, tracking modal.
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { trackAddToCart } from "@/utils/tracking";

export function useCustomerSession({ restaurantId, searchParams, setCustomerPhone, setCustomerName, setCart, API }) {
  const [customerSession, setCustomerSession] = useState(null);
  const [showReorderBanner, setShowReorderBanner] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [trackingOrderId, setTrackingOrderId] = useState(null);

  // Load customer session from localStorage (auth/favorites only — do NOT pre-fill name/phone)
  useEffect(() => {
    const saved = localStorage.getItem(`kaiso_customer_${restaurantId}`);
    if (saved) {
      try {
        const data = JSON.parse(saved);
        // Only restore session for authenticated users (token present)
        // Never pre-fill name/phone — different customers share devices (tablets at tables)
        if (data.phone && data.token) {
          setCustomerPhone(data.phone);
          setCustomerName(data.name || "");
          setCustomerSession(data);
        }
      } catch {}
    }
  }, [restaurantId]);

  // Check URL for tracking parameter
  useEffect(() => {
    const trackParam = searchParams.get("track");
    if (trackParam) {
      setTrackingOrderId(trackParam);
      setShowTrackingModal(true);
    }
  }, [searchParams]);

  const handleCustomerLogin = (session) => {
    setCustomerSession(session);
    setCustomerPhone(session.phone);
    setCustomerName(session.name || "");
    setShowAuthModal(false);
  };

  const handleCustomerLogout = () => {
    setCustomerSession(null);
    setCustomerPhone("");
    setCustomerName("");
  };

  const toggleFavorite = async (productId, shouldAdd) => {
    if (!customerSession?.token) {
      setShowAuthModal(true);
      toast.info("Faça login para salvar favoritos");
      return;
    }
    try {
      const res = await axios.post(`${API}/public/customer/favorites/${restaurantId}`, {
        phone: customerSession.phone,
        token: customerSession.token,
        product_id: productId,
        action: shouldAdd ? "add" : "remove",
      });
      if (res.data.success) {
        setCustomerSession(prev => ({ ...prev, favorites: res.data.favorites }));
        const saved = localStorage.getItem(`kaiso_customer_${restaurantId}`);
        if (saved) {
          const data = JSON.parse(saved);
          data.favorites = res.data.favorites;
          localStorage.setItem(`kaiso_customer_${restaurantId}`, JSON.stringify(data));
        }
        toast.success(shouldAdd ? "Adicionado aos favoritos!" : "Removido dos favoritos");
      }
    } catch {
      toast.error("Erro ao atualizar favoritos");
    }
  };

  const handleReorder = (order) => {
    if (!order?.items?.length) return;
    const newItems = order.items.map(item => ({
      ...item,
      cartId: item.product_id || item.id,
      id: item.product_id || item.id,
      name: item.product_name || item.name,
      price: item.unit_price || item.price,
      quantity: item.quantity || 1,
    }));
    setCart(newItems);
    setShowReorderBanner(false);
    toast.success("Itens do ultimo pedido adicionados ao carrinho!");
    const cartValue = newItems.reduce((s, i) => s + (i.price || 0) * (i.quantity || 1), 0);
    trackAddToCart({ cart_value: cartValue, metadata: { source: "reorder", item_count: newItems.length } });
  };

  return {
    customerSession, setCustomerSession,
    showReorderBanner, setShowReorderBanner,
    showAuthModal, setShowAuthModal,
    showTrackingModal, setShowTrackingModal, trackingOrderId,
    handleCustomerLogin, handleCustomerLogout, toggleFavorite, handleReorder,
  };
}
